import React, { useEffect, useRef, useState, ReactNode } from 'react';
import './StickyScrollContainer.css';

interface StickyScrollContainerProps {
  children: ReactNode;
  settings?: {
    triggerPoint?: number; // Percentage of the viewport height (0-100) where sticky effect triggers
    exitMargin?: number; // Margin in pixels to exit sticky mode at the bottom
    disableMobile?: boolean; // Whether to disable sticky effect on mobile
    mobileBreakpoint?: number; // Mobile breakpoint in pixels
  };
}

export const StickyScrollContainer = ({ 
  children, 
  settings = {
    triggerPoint: 50,
    exitMargin: 0,
    disableMobile: false,
    mobileBreakpoint: 768
  } 
}: StickyScrollContainerProps) => {
  // Container refs
  const containerRef = useRef<HTMLDivElement>(null);
  const stickyContentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // State management
  const [isSticky, setIsSticky] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Configure from settings
  const triggerPoint = settings.triggerPoint || 50;
  const exitMargin = settings.exitMargin || 0;
  const disableMobile = settings.disableMobile || false;
  const mobileBreakpoint = settings.mobileBreakpoint || 768;

  // Check if it's mobile based on window width
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < mobileBreakpoint);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, [mobileBreakpoint]);
  
  // Calculate and set container height based on content
  useEffect(() => {
    if (!contentRef.current) return;
    
    const updateHeight = () => {
      const height = contentRef.current?.scrollHeight || 0;
      setContainerHeight(height);
    };
    
    updateHeight();
    
    // Add resize observer to update height when content changes
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(contentRef.current);
    
    return () => {
      if (contentRef.current) {
        resizeObserver.unobserve(contentRef.current);
      }
    };
  }, [children]);
  
  // Handle scroll events
  useEffect(() => {
    // Skip if mobile and disableMobile is true
    if (isMobile && disableMobile) return;
    
    const handleScroll = () => {
      if (!containerRef.current || !stickyContentRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;
      const viewportHeight = window.innerHeight;
      
      // Calculate the trigger position (default 50% of viewport height)
      const triggerPosition = (viewportHeight * triggerPoint) / 100;
      
      // Check if container top is above trigger position
      if (containerTop <= triggerPosition) {
        // Check if we should exit sticky mode (container bottom is visible)
        if (containerBottom <= viewportHeight + exitMargin) {
          setIsSticky(false);
          
          // Position content at bottom of container when exiting
          if (stickyContentRef.current) {
            stickyContentRef.current.style.position = 'absolute';
            stickyContentRef.current.style.top = 'auto';
            stickyContentRef.current.style.bottom = '0';
          }
        } else {
          setIsSticky(true);
          
          // Set to fixed position when sticky
          if (stickyContentRef.current) {
            stickyContentRef.current.style.position = 'fixed';
            stickyContentRef.current.style.top = '0';
            stickyContentRef.current.style.bottom = 'auto';
          }
        }
      } else {
        setIsSticky(false);
        
        // Reset position when above trigger point
        if (stickyContentRef.current) {
          stickyContentRef.current.style.position = 'relative';
          stickyContentRef.current.style.top = '0';
          stickyContentRef.current.style.bottom = 'auto';
        }
      }
    };
    
    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [triggerPoint, exitMargin, isMobile, disableMobile]);
  
  return (
    <div 
      className="sticky-scroll-container" 
      ref={containerRef}
      style={{ height: `${containerHeight}px` }}
      data-is-sticky={isSticky ? 'true' : 'false'}
    >
      <div 
        className={`sticky-content ${isSticky ? 'is-sticky' : ''}`}
        ref={stickyContentRef}
      >
        <div className="sticky-content-inner" ref={contentRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default StickyScrollContainer;